import { useContext, useMemo, useState, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  TextField,
  OutlinedInput,
  Chip,
  Box,
} from "@mui/material";
import MUIButton from "../../../Component/UI/MUIButton/MUIButton";
import classes from "./Conversations.module.css";
import { DiscoveryContext } from "../discovery-context";
import { OVERVIEW_LLM_LABELS,SITE_SUGGESTIONS } from "../../../utils/constant";
import { ENTITY_LABELS } from "../../../utils/labelMapping";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const ConversationForm = ({ onGenerate, loading }) => {
  const discoveryCtx = useContext(DiscoveryContext);

  const [category, setCategory] = useState(() => discoveryCtx.state.selectedCategory || "");
  const [persona, setPersona] = useState("");
  const [selectedSites, setSelectedSites] = useState([]);
  const [excludeBrandOwnedSources, setExcludeBrandOwnedSources] = useState(false); // NEW

  const personaOptions = useMemo(() => {
    return (discoveryCtx?.persona || [])
      .map((p) =>
        typeof p === "object" && p?.title
          ? { name: p.title, value: p.title }
          : typeof p === "string"
            ? { name: p, value: p }
            : null
      )
      .filter(Boolean);
  }, [discoveryCtx?.persona]);

  useEffect(() => {
    if (persona || personaOptions.length === 0) return;
    setPersona(personaOptions[0].value);
  }, [persona, personaOptions]);

  const handleSiteChange = (e) => {
    const { value } = e.target;
    setSelectedSites(typeof value === "string" ? value.split(",") : value);
  };

  const isDisabled = useMemo(
    () => !category || !persona,
    [category, persona]
  );


  return (
    <>
      <Stack spacing={3} sx={{ mt: 2 }}>
        <TextField
          label={ENTITY_LABELS.brand}
          value={discoveryCtx?.entity || ""}
          fullWidth
          disabled
        />

        {/* Category */}
        <FormControl fullWidth>
          <InputLabel id="category-label">Category</InputLabel>
          <Select
            labelId="category-label"
            value={category}
            label="Category"
            onChange={(e) => setCategory(e.target.value)}
          >
            <MenuItem value="">
              <em>Select Category</em>
            </MenuItem>
            {(discoveryCtx.category || []).map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Persona */}
        <FormControl fullWidth>
          <InputLabel id="persona-label">Persona</InputLabel>
          <Select
            labelId="persona-label"
            value={persona}
            label="Persona"
            onChange={(e) => setPersona(e.target.value)}
          >
            <MenuItem value="">
              <em>Select Persona</em>
            </MenuItem>
            {personaOptions.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Site Suggestions - Multi-Select */}
        <FormControl fullWidth>
          <InputLabel id="site-suggestions-label">Site Suggestions</InputLabel>
          <Select
            labelId="site-suggestions-label"
            multiple
            value={selectedSites}
            onChange={handleSiteChange}
            input={<OutlinedInput label="Site Suggestions" />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => {
                  const site = SITE_SUGGESTIONS.find((s) => s.value === value);
                  return (
                    <Chip
                      key={value}
                      label={site?.name || value}
                      onDelete={(e) => {
                        e.stopPropagation();
                        setSelectedSites((prev) =>
                          prev.filter((item) => item !== value)
                        );
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                  );
                })}
              </Box>
            )}
            MenuProps={MenuProps}
          >
            {SITE_SUGGESTIONS.map((site) => (
              <MenuItem key={site.id} value={site.value}>
                {site.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Exclude Brand Owned Sources - Checkbox */}
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="eliminateBrandContentDiscovery"
            checked={excludeBrandOwnedSources}
            onChange={(e) => setExcludeBrandOwnedSources(e.target.checked)}
          />
          <label
            className="form-check-label"
            htmlFor="eliminateBrandContentDiscovery"
            style={{ marginLeft: "8px", marginBottom: 0 }}
          >
            Eliminate brand content/posts
          </label>
        </div>

      </Stack>

      <div className={classes.actionRow}>
        <MUIButton
          variant="contained"
          color="primary"
          disabled={isDisabled || loading}
          onClick={() =>
            onGenerate({
              category,
              persona,
              siteSuggestions: selectedSites,
              excludeBrandOwnedSources, 
            })
          }
        >
          Get Conversations Data
        </MUIButton>
      </div>
    </>
  );
};

export default ConversationForm;